import Linkify from 'linkify-react';
import * as RPNInput from 'react-phone-number-input';

import { forwardRef } from 'react';

import DatePicker from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';

import {
	AttachmentField,
	BorderTextField,
	DropdownField,
	MultiSelectField,
	SingleSelectField,
	VideoRecordingField,
} from '@/features/form-editing/components';

import { ISFormInputField } from '@/shared/services/forms/forms.types';
import {
	ISUploadFileDraftFileMetadata,
	IVideoFileMetadata,
} from '@/shared/services/submissions/submissions.types';

import { getFileInputAcceptTypesString } from '@/utils/common';
import { cn } from '@/utils/styles';

import InputFieldError from './input-field-error';

interface FillingInputFieldProps {
	inputField: ISFormInputField;
	value:
		| string
		| string[]
		| number
		| ISUploadFileDraftFileMetadata
		| IVideoFileMetadata
		| Blob
		| null;
	error?: string;
	disableActions?: boolean;
	onInputFieldChange: (value: string | string[] | undefined) => void;
	onDateFieldChange: (value: Date | undefined) => void;
	onNumberFieldChange: (value: string | undefined) => void;
	onPhoneFieldChange: (value: RPNInput.Value | undefined) => void;
	onUploadFile?: (file: File) => void;
	onRemoveFile?: () => void;
	onFileError?: (error: string) => void;
	onVideoRecorded?: (videoBlob: Blob, videoPath?: string) => void;
	onVideoRemoved?: () => void;
	getVideoPresignedUrl?: (fieldId: string, fileName: string) => Promise<string>;
	onVideoUploadStateChange?: (isUploading: boolean) => void;
	onVideoEditModeChange?: (isInEditMode: boolean) => void;
	onVideoUnsavedRecordingChange?: (hasUnsavedRecording: boolean) => void;
	onRecordingStart?: () => void;
	onRecordingEnd?: () => void;
	isRecordingBlocked?: boolean;
	viewOnlyMode?: boolean;
	onBlur?: () => void;
	isUploading?: boolean;
	autoUpdateValueOnChangeInitialValue?: boolean;
	labelClassName?: string;
	labelContainerClassName?: string;
}

const FillingInputField = forwardRef<HTMLDivElement, FillingInputFieldProps>(
	(
		{
			inputField,
			value,
			error,
			disableActions = false,
			onInputFieldChange,
			onPhoneFieldChange,
			onDateFieldChange,
			onNumberFieldChange,
			onUploadFile,
			onRemoveFile,
			onFileError,
			onVideoRecorded,
			onVideoRemoved,
			getVideoPresignedUrl,
			onVideoUploadStateChange,
			onVideoEditModeChange,
			onVideoUnsavedRecordingChange,
			onRecordingStart,
			onRecordingEnd,
			isRecordingBlocked = false,
			viewOnlyMode,
			isUploading = false,
			autoUpdateValueOnChangeInitialValue = false,
			onBlur,
			labelClassName,
			labelContainerClassName,
		},
		ref
	) => {
		const {
			question: questionToDisplay,
			description: descriptionToDisplay,
			required: isRequiredField,
		} = inputField;

		const displayInputBox = inputField.type === 'SHORT_TEXT' || inputField.type === 'LINK';
		const displayNumberInputBox = inputField.type === 'NUMBER';
		const displayTextareaBox = inputField.type === 'LONG_TEXT';
		const displayAttachmentBox = inputField.type === 'FILE_UPLOAD';
		const displayVideoBox = inputField.type === 'VIDEO';
		const displayDropdownBox = inputField.type === 'DROPDOWN';
		const displaySingleSelectBox = inputField.type === 'SINGLE_SELECT';
		const displayMultiSelectBox = inputField.type === 'MULTI_SELECT';
		const displayPhoneInputBox = inputField.type === 'PHONE_NUMBER';
		const displayDatePickerBox = inputField.type === 'DATE';

		return (
			<div
				ref={ref}
				className={cn(
					'group/input-field relative w-full border-transparent bg-white transition-all'
				)}
			>
				<div className={'flex w-full flex-col gap-0 py-2 focus-visible:outline-none'}>
					<div className={cn('mb-3 [&_a]:text-primary-blue', labelContainerClassName)}>
						<Label className={cn('text-lg font-semibold', labelClassName)}>
							<span className="inline-block whitespace-pre-line">
								<Linkify options={{ target: '_blank' }}>{questionToDisplay}</Linkify>
								<span className="font-normal text-primary/70">{`${isRequiredField ? ' *' : ''}`}</span>
							</span>
						</Label>
						{typeof descriptionToDisplay === 'string' &&
							descriptionToDisplay.trim() !== '<p></p>' && (
								<div className="mb-1 mt-2 whitespace-pre-line text-sm font-normal text-secondary">
									<Linkify options={{ target: '_blank' }}>{descriptionToDisplay}</Linkify>
								</div>
							)}
					</div>

					{displayInputBox && (
						<BorderTextField
							type="INPUT"
							editMode={true}
							fillMode={true}
							disabled={disableActions}
							customInputClassName="w-[300px]"
							onChangeValue={(v) => onInputFieldChange(inputField?.type === 'LINK' ? v.trim() : v)}
							value={value ? String(value) : undefined}
							autoUpdateValueOnChangeInitialValue={autoUpdateValueOnChangeInitialValue}
							debounceDuration={0}
							onBlur={onBlur}
						/>
					)}

					{displayNumberInputBox && (
						<BorderTextField
							type="NUMBER_INPUT"
							editMode={true}
							fillMode={true}
							disabled={disableActions}
							onChangeValue={onNumberFieldChange}
							value={value ? String(value) : undefined}
							autoUpdateValueOnChangeInitialValue={autoUpdateValueOnChangeInitialValue}
							debounceDuration={0}
							onBlur={onBlur}
						/>
					)}

					{displayTextareaBox && (
						<BorderTextField
							type="TEXTAREA"
							editMode={true}
							fillMode={true}
							disabled={disableActions}
							onChangeValue={onInputFieldChange}
							value={value ? String(value) : undefined}
							autoUpdateValueOnChangeInitialValue={autoUpdateValueOnChangeInitialValue}
							debounceDuration={0}
							onBlur={onBlur}
						/>
					)}

					{displayAttachmentBox && (
						<AttachmentField
							fieldId={inputField.id}
							attachedFileName={(value as ISUploadFileDraftFileMetadata)?.originalFilename}
							editMode={false}
							acceptedFileTypes={getFileInputAcceptTypesString(inputField.fileTypes ?? [])}
							onUploadFile={onUploadFile}
							onRemoveFile={onRemoveFile}
							isUploading={isUploading}
							disabled={disableActions}
							onFileError={onFileError}
						/>
					)}

					{displayVideoBox && (
						<VideoRecordingField
							fieldId={inputField.id}
							editMode={false}
							disabled={disableActions}
							onVideoRecorded={onVideoRecorded}
							onVideoRemoved={onVideoRemoved}
							getVideoPresignedUrl={getVideoPresignedUrl}
							recordedVideo={value as Blob | null}
							formId={inputField.formId}
							formName={inputField.formName}
							userFirstName={inputField.userFirstName}
							onUploadStateChange={onVideoUploadStateChange || (() => {})}
							onEditModeChange={onVideoEditModeChange}
							onUnsavedRecordingChange={onVideoUnsavedRecordingChange}
							viewOnlyMode={viewOnlyMode}
							onRecordingStart={onRecordingStart}
							onRecordingEnd={onRecordingEnd}
							isRecordingBlocked={isRecordingBlocked}
						/>
					)}

					{displayDropdownBox && (
						<DropdownField
							options={inputField.options ?? []}
							editMode={false}
							isFocusedInput={false}
							onSelectOption={onInputFieldChange}
							selectedOption={typeof value === 'string' ? value : undefined}
							disabled={disableActions}
							allowOther={inputField.allowOther}
						/>
					)}

					{displaySingleSelectBox && (
						<SingleSelectField
							options={inputField.options ?? []}
							editMode={false}
							isFocusedInput={false}
							onSelectOption={onInputFieldChange}
							selectedOption={typeof value === 'string' ? value : undefined}
							allowOther={inputField.allowOther}
							disabled={disableActions}
						/>
					)}

					{displayMultiSelectBox && (
						<MultiSelectField
							options={inputField.options ?? []}
							editMode={false}
							isFocusedInput={false}
							onSelectOptions={onInputFieldChange}
							selectedOptions={Array.isArray(value) ? value : []}
							disabled={disableActions}
							allowOther={inputField.allowOther}
						/>
					)}

					{displayPhoneInputBox && (
						<PhoneInput
							className="w-full max-w-[300px]"
							defaultCountry={'IN'}
							onChange={onPhoneFieldChange}
							value={value ? String(value) : undefined}
							disabled={disableActions}
						/>
					)}

					{displayDatePickerBox && (
						<DatePicker
							selectedDate={
								typeof value === 'string' && !isNaN(Date.parse(value))
									? new Date(value)
									: undefined
							}
							onSelectDate={onDateFieldChange}
							disabled={disableActions}
						/>
					)}
					<InputFieldError errorMessage={error} />
				</div>
			</div>
		);
	}
);

FillingInputField.displayName = 'FillingInputField';

export default FillingInputField;
